import isNil from 'lodash/isNil';
import type { SLA_DISPLAY_FORMAT } from '@atlassian/jira-servicedesk-sla-panel/src/common/types';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import type {
	FormValues,
	FormGoal,
	PayloadGoal,
	PayloadGoals,
	PayloadMetric,
	Definition,
	SubGoals,
} from '../../../../../common/types';
import {
	ROW_INDEX_TRACKER,
	DEFAULT_GOAL_ROW_INDEX_TRACKER,
	DEFAULT,
	NEW_AND_MODIFIED,
	EXISTING_AND_MODIFIED,
	NEW,
} from '../../../../../common/ui/constants.tsx';
import { durationToMilliseconds } from '../../../../../common/utils/duration/index.tsx';
import { toGoalField, toGoalJqlFieldName } from '../../../../../common/utils/goal/field';

// return default goal
export const getDefaultGoal = (
	formFields: FormValues,
	formFiledEntities: [string, unknown][],
	formatMessage: FormatMessage,
	isSLAHierarchicalGoalsEnabled = false,
): Partial<PayloadGoal> => {
	// @ts-expect-error - TS2769 - No overload matches this call.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const defaultGoal = formFiledEntities.find(([key]: [any]) =>
		RegExp(DEFAULT_GOAL_ROW_INDEX_TRACKER).test(key),
	);

	const [defaultGoalKey] = defaultGoal || [];
	// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
	const defaultGoalId = defaultGoalKey.slice(DEFAULT_GOAL_ROW_INDEX_TRACKER.length);

	const goal = formFields[toGoalField(defaultGoalId)];
	const {
		fieldState,
		calendarName,
		duration: durationHumanReadable,
		subGoals = [],
		...restGoal
	} = goal;
	const duration = isNil(durationHumanReadable)
		? ''
		: durationToMilliseconds(durationHumanReadable, formatMessage);

	// it's new metric with default goal
	// remove some of the properties which are EMPTY
	if (defaultGoalId === DEFAULT) {
		const filteredEntities = Object.entries({
			duration,
			...restGoal,
		}).filter(
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			([key, value]: [any, any]) =>
				(['calendarId', 'duration', 'defaultGoal'].includes(key) &&
					value !== undefined &&
					value !== '') ||
				key === 'jqlQuery',
		);

		const newDefaultGoal = Object.fromEntries(filteredEntities);

		return newDefaultGoal;
	}

	if (isSLAHierarchicalGoalsEnabled) {
		return { ...restGoal, duration, subGoals };
	}

	return { ...restGoal, duration };
};

// convert duration to millisecond
// remove fields which are not need  id
export const subGoalReducer = (subGoals: SubGoals, formatMessage: FormatMessage): SubGoals =>
	// @ts-expect-error - TS2322
	subGoals.map((subGoal) => {
		// @ts-expect-error - TS2339 - fieldState is not defined on SubGoal type
		const { id, fieldState, duration: durationHumanReadable, ...rest } = subGoal;
		const duration = isNil(durationHumanReadable)
			? ''
			: durationToMilliseconds(durationHumanReadable, formatMessage);

		if (fieldState === 'NEW') {
			return { ...rest, duration };
		}
		return { id, ...rest, duration };
	});

// convert duration to millisecond
// add new goal base on fieldState = NEW_AND_MODIFIED
// remove fields which are not need  id, fieldState and timeMetricId for new goal
export const goalReducer = (
	goalList: PayloadGoals,
	goal: FormGoal,
	formatMessage: FormatMessage,
	isSLAHierarchicalGoalsEnabled = false,
	formValues: FormValues,
): PayloadGoals => {
	const {
		id,
		fieldState,
		duration: durationHumanReadable,
		timeMetricId,
		subGoals = [],
		...restGoalFields
	} = goal;

	const duration = isNil(durationHumanReadable)
		? ''
		: // @ts-expect-error - TS2345 - Argument of type 'string | number' is not assignable to parameter of type 'string'.
			durationToMilliseconds(durationHumanReadable, formatMessage);

	if (isSLAHierarchicalGoalsEnabled) {
		const newSubGoals = subGoalReducer(subGoals, formatMessage);

		// @ts-expect-error - TS2345 - Argument of type 'string | number' is not assignable to parameter of type 'string'
		const jqlQuery = formValues[toGoalJqlFieldName(id, true)];
		if (fieldState && (fieldState === NEW || fieldState === NEW_AND_MODIFIED)) {
			goalList.push({ ...restGoalFields, duration, jqlQuery, subGoals: newSubGoals });
		}

		if (fieldState === undefined || (fieldState && fieldState === EXISTING_AND_MODIFIED)) {
			goalList.push({
				id,
				...restGoalFields,
				timeMetricId,
				duration,
				jqlQuery,
				subGoals: newSubGoals,
			});
		}
	} else {
		if (fieldState && fieldState === NEW_AND_MODIFIED) {
			goalList.push({ ...restGoalFields, duration });
		}

		if (fieldState === undefined || (fieldState && fieldState === EXISTING_AND_MODIFIED)) {
			goalList.push({ id, ...restGoalFields, timeMetricId, duration });
		}
	}

	return goalList;
};

export const getGoals = (
	formFields: FormValues,
	formatMessage: FormatMessage,
	isSLAHierarchicalGoalsEnabled = false,
): PayloadGoals => {
	const formFiledEntities = Object.entries(formFields);
	const goalKeys = formFiledEntities
		// @ts-expect-error - TS2769 - No overload matches this call.
		.filter(([key]: [any]) => RegExp(ROW_INDEX_TRACKER).test(key)) // eslint-disable-line @typescript-eslint/no-explicit-any
		.sort(([, orderA]: [any, any], [, orderB]: [any, any]) => Number(orderA) - Number(orderB)) // eslint-disable-line @typescript-eslint/no-explicit-any
		// @ts-expect-error - TS2345 - Argument of type '([key]: [any]) => any' is not assignable to parameter of type '(value: [string, any], index: number, array: [string, any][]) => any'.
		.map(([key]: [any]) => key); // eslint-disable-line @typescript-eslint/no-explicit-any

	const goalIds = goalKeys.map((goalKey) => goalKey.slice(ROW_INDEX_TRACKER.length));

	const allGoals = goalIds.map((id: string) => formFields[toGoalField(id)]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const goals = allGoals.reduce<Array<any>>(
		(goalList: PayloadGoals, goal: FormGoal) =>
			goalReducer(goalList, goal, formatMessage, isSLAHierarchicalGoalsEnabled, formFields),
		[],
	);
	const defaultGoal = getDefaultGoal(
		formFields,
		formFiledEntities,
		formatMessage,
		isSLAHierarchicalGoalsEnabled,
	);

	return [...goals, defaultGoal];
};

export const getDefinition = (formFields: FormValues): Definition => {
	const { start, pause, stop } = formFields;
	return {
		start,
		pause,
		stop,
		inconsistent: false,
	};
};

export const getName = (formFields: FormValues) => formFields?.name?.label;

export const getSlaDisplayFormat = (formFields: FormValues): SLA_DISPLAY_FORMAT => {
	const { slaDisplayFormat } = formFields;
	return slaDisplayFormat;
};

export const getPayload = (
	formFields: FormValues,
	formatMessage: FormatMessage,
	isSLAHierarchicalGoalsEnabled = false,
): PayloadMetric => ({
	name: getName(formFields),
	config: {
		definition: getDefinition(formFields),
		goals: getGoals(formFields, formatMessage, isSLAHierarchicalGoalsEnabled),
		slaDisplayFormat: getSlaDisplayFormat(formFields),
	},
});
